import {ToolExecuteParams, ToolResult} from 'common/src/modules/shared/copilot/copilot.types';
import tool from 'common/src/modules/shared/copilot/utils/creators/tool';
import toolResult from 'common/src/modules/shared/copilot/utils/creators/toolResult';
import toolError from 'common/src/modules/shared/copilot/utils/toolError';
import {CLStore} from 'common/src/redux/store';
import {z} from 'zod';
import campaignsApi from '../../campaigns.api';
import fetchCampaigns from '../../store/campaignsLibrary/thunks/fetchCampaigns';
import {CampaignsAgentsToolsNames} from '../copilotCampaigns.enums';

function duplicateCampaignTool({store}: {store: CLStore}) {
  const inputSchema = z.object({
    campaignIds: z
      .array(z.number())
      .min(1)
      .describe('Array of campaign IDs to duplicate. Must contain at least one campaign ID.'),
    includeAdGroups: z
      .boolean()
      .optional()
      .nullable()
      .describe(
        'Whether to include ad groups when duplicating social campaigns. Defaults to false.',
      ),
  });

  async function execute({input}: ToolExecuteParams<typeof inputSchema>): Promise<ToolResult> {
    const {campaignIds, includeAdGroups} = input;

    try {
      const duplicateRequests = campaignIds.map((id) =>
        campaignsApi.duplicateCampaign(id, includeAdGroups ?? false),
      );

      await Promise.all(duplicateRequests);

      store.dispatch(fetchCampaigns());

      const campaignWord = campaignIds.length === 1 ? 'Campaign' : 'Campaigns';
      const campaignIdsList = campaignIds.join(', ');

      return toolResult(
        `${campaignWord} duplicated successfully. Duplicated campaign ID(s): ${campaignIdsList}. ` +
          `The campaigns library has been refreshed to show the new duplicated campaigns.`,
      );
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'An unknown error occurred';

      return toolError(
        `Failed to duplicate campaigns: ${errorMessage}. ` +
          `Campaign ID(s) attempted: ${campaignIds.join(', ')}.`,
      );
    }
  }

  return tool({
    name: CampaignsAgentsToolsNames.DuplicateCampaigns,
    displayName: 'Duplicating campaign',
    description: `
    Duplicates one or more campaigns in the system.

    ## When to Call
    Call this tool when the user wants to duplicate existing campaigns.
    You can duplicate single or multiple campaigns in one call.

    ## Parameters
    - campaignIds: Array of campaign IDs to duplicate (required, must have at least one ID)
    - includeAdGroups: Whether to include ad groups for social campaigns (optional, defaults to false)

    ## Important
    - The tool will duplicate all campaigns in parallel
    - After duplication, the campaigns library will be automatically refreshed
    - Duplicated campaigns will have the same configuration as the original campaigns
    `.trim(),
    execute,
    inputSchema,
  });
}

export default duplicateCampaignTool;
